import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { getApiError } from '../utils/apiError';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Mail, Users, FileText, ChevronRight, ChevronLeft, Play, Pause, Loader2, ShieldCheck, Eye, X, UserPlus, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import TagSelect from '../components/TagSelect';
import RichTextEditor from '../components/RichTextEditor';
import DOMPurify from 'dompurify';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_COLORS = {
    Draft: 'bg-slate-100 text-slate-700',
    Scheduled: 'bg-amber-100 text-amber-800',
    Running: 'bg-emerald-100 text-emerald-800',
    Paused: 'bg-orange-100 text-orange-800',
    Completed: 'bg-slate-100 text-slate-600'
};

const VARIABLE_OPTIONS = [
    { label: 'First Name', value: 'first_name' },
    { label: 'Full Name', value: 'full_name' },
    { label: 'Company', value: 'company' },
    { label: 'Job Title', value: 'job_title' },
    { label: 'Email', value: 'email' }
];

const SEND_INTERVAL_PRESETS = [
    { value: 'immediate', label: 'Immediate (0 sec)', min: 0, max: 0, warning: true },
    { value: 'fast', label: 'Fast (30–90 sec)', min: 30, max: 90 },
    { value: 'standard', label: 'Standard (1–5 min)', min: 60, max: 300 },
    { value: 'conservative', label: 'Conservative (2–10 min)', min: 120, max: 600 },
    { value: 'custom', label: 'Custom' }
];

const getPresetFromConfig = (config) => {
    const min = config?.min_delay_seconds ?? null;
    const max = config?.max_delay_seconds ?? null;
    if (min == null && max == null) return 'standard';
    const preset = SEND_INTERVAL_PRESETS.find(p => p.value !== 'custom' && p.min === min && p.max === max);
    return preset ? preset.value : 'custom';
};

const INITIAL_CAMPAIGN_FORM = {
    name: '',
    sender_account_ids: [],
    subject: '',
    body_html: '',
    filter_tags: [],
    filter_mode: 'all',
    auto_enroll_tags: [],
    paste_emails: '',
    audienceList: [],
    config: { daily_limit: 50, start_time: '09:00', end_time: '17:00', timezone: 'America/New_York', ignore_business_hours: false, min_delay_seconds: 60, max_delay_seconds: 300 },
    bounce_threshold: 3,
    variants: [],
    sendIntervalPreset: 'standard',
    ignore_status_filters: false
};

const CampaignsPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [campaigns, setCampaigns] = useState([]);
    const [wizardOpen, setWizardOpen] = useState(false);
    const [wizardStep, setWizardStep] = useState(1);
    const [accounts, setAccounts] = useState([]);
    const [leads, setLeads] = useState([]);
    const [tags, setTags] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [deliverabilityResult, setDeliverabilityResult] = useState(null);
    const [previewLead, setPreviewLead] = useState(null);

    const [form, setForm] = useState(INITIAL_CAMPAIGN_FORM);
    const [enrichedLeads, setEnrichedLeads] = useState({});

    useEffect(() => {
        fetchCampaigns();
        fetchAccounts();
        fetchTags();
        fetchTemplates();
    }, []);

    useEffect(() => {
        if (wizardStep !== 2) return;
        let cancelled = false;
        fetchLeads('mine', []).then((data) => {
            if (!cancelled) setLeads(data ?? []);
        });
        return () => { cancelled = true; };
    }, [wizardStep]);

    useEffect(() => {
        if (previewLead) {
            const list = form.audienceList || [];
            const found = list.some((item) => String(item.leadId) === String(previewLead.leadId));
            if (!found) {
                setPreviewLead(null);
            }
        }
    }, [form.audienceList]);

    useEffect(() => {
        const list = form.audienceList || [];
        if (list.length === 0) {
            setEnrichedLeads({});
            return;
        }
        const leadIds = list.map((x) => x.leadId);
        axios.post(`${API_URL}/campaigns/audience-enrich`, { lead_ids: leadIds })
            .then(({ data }) => setEnrichedLeads(data.enriched || {}))
            .catch(() => setEnrichedLeads({}));
    }, [form.audienceList]);

    const fetchCampaigns = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/campaigns`);
            setCampaigns(data);
        } catch (e) {
            toast.error('Failed to fetch campaigns');
        }
    };

    const fetchAccounts = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/accounts`);
            setAccounts(data);
        } catch (e) {
            toast.error('Failed to fetch accounts');
        }
    };

    const fetchLeads = async (filter = 'mine', tagIds = []) => {
        try {
            const params = new URLSearchParams();
            if (filter !== 'all') params.append('filter', filter);
            if (tagIds?.length) params.append('tags', tagIds.join(','));
            const { data } = await axios.get(`${API_URL}/leads${params.toString() ? '?' + params.toString() : ''}`);
            return data;
        } catch (e) {
            toast.error('Failed to fetch leads');
            return [];
        }
    };

    const addToAudience = (newItems) => {
        const seen = new Set((form.audienceList || []).map(x => x.leadId));
        const toAdd = newItems.filter(x => !seen.has(x.leadId));
        toAdd.forEach(x => seen.add(x.leadId));
        setForm({ ...form, audienceList: [...(form.audienceList || []), ...toAdd] });
    };

    const getLeadEnrichment = (item) => {
        const e = enrichedLeads[item.leadId];
        return {
            skip_reason: item.skip_reason ?? e?.skip_reason,
            status_label: item.status_label ?? e?.status_label,
            has_contacted: item.has_contacted ?? e?.has_contacted ?? false
        };
    };

    const addMyLeads = async () => {
        const data = await fetchLeads('mine', []);
        const eligible = data.filter(l => l.email);
        addToAudience(eligible.map(l => ({ leadId: l.id, name: l.name, email: l.email })));
        toast.success(`Added ${eligible.length} lead${eligible.length !== 1 ? 's' : ''} assigned to you`);
    };

    const addByTags = async () => {
        if (!form.filter_tags?.length) {
            toast.error('Select at least one tag');
            return;
        }
        const data = await fetchLeads('all', form.filter_tags);
        const eligible = data.filter(l => l.email);
        addToAudience(eligible.map(l => ({ leadId: l.id, name: l.name, email: l.email })));
        toast.success(`Added ${eligible.length} lead${eligible.length !== 1 ? 's' : ''} matching tags`);
    };

    const addFromPaste = async () => {
        const emails = (form.paste_emails || '').replace(/,/g, '\n').split('\n').map(e => e.trim().toLowerCase()).filter(Boolean);
        if (!emails.length) {
            toast.error('Paste at least one email address');
            return;
        }
        setLoading(true);
        try {
            const { data } = await axios.post(`${API_URL}/campaigns/resolve-emails`, { emails: emails.join('\n') });
            const mapped = (data.leads || []).map(l => ({
                leadId: l.lead_id, name: l.name, email: l.email,
                status_label: l.status_label, skip_reason: l.skip_reason, has_contacted: l.has_contacted
            }));
            const seen = new Set((form.audienceList || []).map(x => x.leadId));
            const toAdd = mapped.filter(x => !seen.has(x.leadId));
            setForm(prev => ({ ...prev, audienceList: [...(prev.audienceList || []), ...toAdd], paste_emails: '' }));
            toast.success(`Added ${toAdd.length} email${toAdd.length !== 1 ? 's' : ''} (new leads created if needed)`);
        } catch (e) {
            toast.error(getApiError(e, 'Failed to resolve emails'));
        } finally {
            setLoading(false);
        }
    };

    const removeFromAudience = (leadId) => {
        setForm({ ...form, audienceList: (form.audienceList || []).filter(x => x.leadId !== leadId) });
    };

    const fetchTags = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/tags`);
            setTags(data);
        } catch (e) {
            toast.error('Failed to fetch tags');
        }
    };

    const fetchTemplates = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/templates`);
            setTemplates(data);
        } catch (e) {
            toast.error('Failed to fetch templates');
        }
    };

    const handleCreateCampaign = async () => {
        if (!form.name.trim()) {
            toast.error('Campaign name is required');
            return;
        }
        if (wizardStep < 3) {
            if (wizardStep === 2 && (!form.audienceList || form.audienceList.length === 0)) {
                toast.error('Add at least one recipient to the audience list before continuing');
                return;
            }
            setWizardStep(wizardStep + 1);
            return;
        }
        if (!form.sender_account_ids?.length) {
            toast.error('Select at least one sender account');
            return;
        }
        if (!form.subject?.trim() || !form.body_html?.trim()) {
            toast.error('Subject and body are required');
            return;
        }
        const leadIds = (form.audienceList || []).map(x => x.leadId);
        if (!leadIds.length) {
            toast.error('Add at least one recipient to the audience list');
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API_URL}/campaigns`, {
                name: form.name,
                sender_account_ids: form.sender_account_ids,
                subject: form.subject,
                body_html: form.body_html,
                lead_ids: leadIds,
                filter_tags: form.filter_tags?.length ? form.filter_tags : null,
                filter_mode: form.filter_mode,
                auto_enroll_tags: form.auto_enroll_tags?.length ? form.auto_enroll_tags : null,
                config: form.config,
                bounce_threshold: form.bounce_threshold,
                variants: form.variants,
                ignore_status_filters: form.ignore_status_filters ?? false
            });
            toast.success('Campaign created');
            setWizardOpen(false);
            setWizardStep(1);
            setForm(INITIAL_CAMPAIGN_FORM);
            fetchCampaigns();
        } catch (e) {
            toast.error(getApiError(e, 'Failed to create campaign'));
        } finally {
            setLoading(false);
        }
    };

    const handleEnrollLeads = async (campaignId, leadIds) => {
        try {
            const { data } = await axios.post(`${API_URL}/campaigns/${campaignId}/enroll`, { lead_ids: leadIds });
            toast.success(`Enrolled ${data.enrolled} leads`);
            fetchCampaigns();
        } catch (e) {
            toast.error(getApiError(e, 'Failed to enroll leads'));
        }
    };

    const handleEnrollPaste = async (campaignId, emails) => {
        try {
            const { data } = await axios.post(`${API_URL}/campaigns/${campaignId}/enroll-paste`, { emails });
            toast.success(`Created ${data.created} leads, enrolled ${data.enrolled}`);
            fetchCampaigns();
        } catch (e) {
            toast.error(getApiError(e, 'Failed to enroll'));
        }
    };

    const handleStart = async (id) => {
        try {
            await axios.post(`${API_URL}/campaigns/${id}/start`);
            toast.success('Campaign started');
            fetchCampaigns();
        } catch (e) {
            toast.error(getApiError(e, 'Failed to start'));
        }
    };

    const handlePause = async (id) => {
        try {
            await axios.post(`${API_URL}/campaigns/${id}/pause`);
            toast.success('Campaign paused');
            fetchCampaigns();
        } catch (e) {
            toast.error(getApiError(e, 'Failed to pause'));
        }
    };

    const handleResume = async (id) => {
        try {
            await axios.post(`${API_URL}/campaigns/${id}/resume`);
            toast.success('Campaign resumed');
            fetchCampaigns();
        } catch (e) {
            toast.error(getApiError(e, 'Failed to resume'));
        }
    };

    const checkDeliverability = async () => {
        setLoading(true);
        setDeliverabilityResult(null);
        try {
            const { data } = await axios.post(`${API_URL}/campaigns/check-deliverability`, { subject: form.subject, body: form.body_html });
            setDeliverabilityResult(data);
        } catch (e) {
            toast.error('Deliverability check failed');
        } finally {
            setLoading(false);
        }
    };

    const escapeHtml = (str) => {
        if (str == null || str === '') return '';
        return String(str)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    };

    const processVariablesForPreview = (text, lead) => {
        if (!text) return '';
        const first_name = escapeHtml(lead?.name ? lead.name.trim().split(' ')[0] : 'there');
        const full_name = escapeHtml(lead?.name || '');
        const company = escapeHtml(lead?.company || '');
        const job_title = escapeHtml(lead?.job_title || '');
        const email = escapeHtml(lead?.email || '');
        return text
            .replace(/\{\{first_name\}\}/g, first_name)
            .replace(/\{\{full_name\}\}/g, full_name)
            .replace(/\{\{name\}\}/g, full_name)
            .replace(/\{\{company\}\}/g, company)
            .replace(/\{\{job_title\}\}/g, job_title)
            .replace(/\{\{email\}\}/g, email)
            .replace(/\{\{[^}]+(?:\|[^}]*)?\}\}/g, ''); // fallback for other vars
    };

    const getProgress = (c) => {
        const enrolled = c.enrolled_count ?? c.total_leads ?? c.stats?.sent;
        if (!enrolled) return 0;
        const sent = c.stats?.sent || 0;
        return Math.min(100, (sent / enrolled) * 100);
    };

    return (
        <div className="p-6 md:p-8 lg:p-10 space-y-8">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-2">Campaigns</h1>
                    <p className="text-sm leading-relaxed text-slate-600">Bulk email outreach with smart throttling and bounce protection</p>
                </div>
                <Button
                    onClick={() => { setWizardOpen(true); setWizardStep(1); setForm(INITIAL_CAMPAIGN_FORM); }}
                    className="rounded-lg font-medium bg-slate-900 hover:bg-slate-800 text-white"
                >
                    <Plus className="h-4 w-4 mr-2" />
                    Create Campaign
                </Button>
            </div>

            <div className="grid gap-4">
                {campaigns.map((c) => (
                    <Card
                        key={c.id}
                        className="p-4 rounded-xl border-slate-200 cursor-pointer hover:border-slate-300 hover:shadow-sm transition-all"
                        onClick={() => navigate(`/campaigns/${c.id}`)}
                    >
                        <div className="flex items-center justify-between flex-wrap gap-4">
                            <div>
                                <div className="flex items-center gap-2">
                                    <h3 className="font-semibold text-slate-900">{c.name}</h3>
                                    <Badge className={STATUS_COLORS[c.status] || 'bg-slate-100'}>{c.status}</Badge>
                                </div>
                                <p className="text-sm text-slate-500 mt-1">
                                    Sent: {c.stats?.sent || 0} | Opened: {c.stats?.opened || 0} | Replied: {c.stats?.replied || 0} | Bounced: {c.stats?.bounced || 0}
                                </p>
                                {(c.last_updated_by_email || c.last_updated_at) && (
                                    <p className="text-xs text-slate-400 mt-0.5">
                                        Last updated{c.last_updated_by_email ? ` by ${c.last_updated_by_email}` : ''}{c.last_updated_at ? ` at ${new Date(c.last_updated_at).toLocaleString()}` : ''}
                                    </p>
                                )}
                                <div className="mt-2 w-48">
                                    <Progress value={getProgress(c)} className="h-2" />
                                </div>
                            </div>
                            <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                                {c.status === 'Draft' && (
                                    <Button size="sm" onClick={() => handleStart(c.id)}>
                                        <Play className="h-4 w-4 mr-1" /> Start
                                    </Button>
                                )}
                                {c.status === 'Running' && (
                                    <Button size="sm" variant="outline" onClick={() => handlePause(c.id)}>
                                        <Pause className="h-4 w-4 mr-1" /> Pause
                                    </Button>
                                )}
                                {c.status === 'Paused' && (
                                    <Button size="sm" onClick={() => handleResume(c.id)}>
                                        <Play className="h-4 w-4 mr-1" /> Resume
                                    </Button>
                                )}
                            </div>
                        </div>
                    </Card>
                ))}
            </div>

            <Dialog open={wizardOpen} onOpenChange={setWizardOpen}>
                <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle>Create Campaign</DialogTitle>
                        <DialogDescription>Step {wizardStep} of 3</DialogDescription>
                    </DialogHeader>
                    <div className="flex-1 overflow-y-auto space-y-6 py-4">
                        {wizardStep === 1 && (
                            <>
                                <div>
                                    <Label>Campaign Name</Label>
                                    <Input
                                        value={form.name}
                                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                                        placeholder="e.g., Retail CTOs - Jan Cohort"
                                        className="rounded-lg mt-1"
                                    />
                                </div>
                                <div>
                                    <Label className="flex items-center gap-2"><Mail className="h-4 w-4" /> Sender Accounts (Step 1)</Label>
                                    <p className="text-sm text-slate-500 mb-2">Select multiple accounts for load balancing</p>
                                    <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-2">
                                        {accounts.filter(a => a.is_active).map((acc) => (
                                            <div key={acc.id} className="flex items-center gap-2">
                                                <Checkbox
                                                    checked={form.sender_account_ids?.includes(acc.id)}
                                                    onCheckedChange={(checked) => {
                                                        const ids = form.sender_account_ids || [];
                                                        setForm({
                                                            ...form,
                                                            sender_account_ids: checked ? [...ids, acc.id] : ids.filter(id => id !== acc.id)
                                                        });
                                                    }}
                                                />
                                                <span className="text-sm">{acc.email}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 mb-2">
                                    <Switch
                                        id="ignore-hours-wizard"
                                        checked={form.config?.ignore_business_hours ?? false}
                                        onCheckedChange={(checked) => setForm({ ...form, config: { ...form.config, ignore_business_hours: !!checked } })}
                                    />
                                    <Label htmlFor="ignore-hours-wizard" className="font-normal">Send 24/7 (ignore business hours)</Label>
                                </div>
                                {!form.config?.ignore_business_hours && (
                                    <div className="grid grid-cols-3 gap-4 mb-4">
                                        <div>
                                            <Label>Start time</Label>
                                            <Input type="time" value={form.config?.start_time || '09:00'} onChange={(e) => setForm({ ...form, config: { ...form.config, start_time: e.target.value || '09:00' } })} className="rounded-lg mt-1" />
                                        </div>
                                        <div>
                                            <Label>End time</Label>
                                            <Input type="time" value={form.config?.end_time || '17:00'} onChange={(e) => setForm({ ...form, config: { ...form.config, end_time: e.target.value || '17:00' } })} className="rounded-lg mt-1" />
                                        </div>
                                        <div>
                                            <Label>Timezone</Label>
                                            <Select value={form.config?.timezone || 'America/New_York'} onValueChange={(v) => setForm({ ...form, config: { ...form.config, timezone: v } })}>
                                                <SelectTrigger className="rounded-lg mt-1">
                                                    <SelectValue placeholder="Timezone" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="America/New_York">America/New_York (EST)</SelectItem>
                                                    <SelectItem value="America/Los_Angeles">America/Los_Angeles (PST)</SelectItem>
                                                    <SelectItem value="Europe/London">Europe/London (GMT)</SelectItem>
                                                    <SelectItem value="Europe/Paris">Europe/Paris (CET)</SelectItem>
                                                    <SelectItem value="Asia/Kolkata">Asia/Kolkata (IST)</SelectItem>
                                                    <SelectItem value="Asia/Tokyo">Asia/Tokyo (JST)</SelectItem>
                                                    <SelectItem value="Australia/Sydney">Australia/Sydney (AEST)</SelectItem>
                                                    <SelectItem value="UTC">UTC</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                )}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label>Daily Limit</Label>
                                        <Input type="number" value={form.config?.daily_limit || 50} onChange={(e) => setForm({ ...form, config: { ...form.config, daily_limit: parseInt(e.target.value) || 50 } })} className="rounded-lg mt-1" />
                                    </div>
                                    <div>
                                        <Label>Bounce Threshold (%)</Label>
                                        <Input type="number" value={form.bounce_threshold} onChange={(e) => setForm({ ...form, bounce_threshold: parseInt(e.target.value) || 3 })} className="rounded-lg mt-1" />
                                    </div>
                                </div>
                                <div>
                                    <Label>Gap between emails</Label>
                                    <p className="text-xs text-slate-500 mt-0.5 mb-2">Delay between each email sent. Sending too frequently can land in spam.</p>
                                    <Select
                                        value={form.sendIntervalPreset ?? getPresetFromConfig(form.config)}
                                        onValueChange={(v) => {
                                            const preset = SEND_INTERVAL_PRESETS.find(p => p.value === v);
                                            if (preset && preset.value !== 'custom') {
                                                setForm({ ...form, sendIntervalPreset: preset.value, config: { ...form.config, min_delay_seconds: preset.min, max_delay_seconds: preset.max } });
                                            } else {
                                                const min = form.config?.min_delay_seconds ?? 90;
                                                const max = form.config?.max_delay_seconds ?? 180;
                                                setForm({ ...form, sendIntervalPreset: 'custom', config: { ...form.config, min_delay_seconds: min, max_delay_seconds: max } });
                                            }
                                        }}
                                    >
                                        <SelectTrigger className="rounded-lg mt-1">
                                            <SelectValue placeholder="Select interval" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {SEND_INTERVAL_PRESETS.map((p) => (
                                                <SelectItem key={p.value} value={p.value}>
                                                    {p.label}{p.warning ? ' ⚠️' : ''}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {(form.sendIntervalPreset ?? getPresetFromConfig(form.config)) === 'immediate' && (
                                        <div className="mt-2 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                                            <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
                                            <p>Emails sent too frequently can trigger spam filters and hurt deliverability. Consider using Standard or Conservative for better inbox placement.</p>
                                        </div>
                                    )}
                                    {(form.sendIntervalPreset ?? getPresetFromConfig(form.config)) === 'custom' && (
                                        <div className="grid grid-cols-2 gap-4 mt-2">
                                            <div>
                                                <Label>Minimum delay (seconds)</Label>
                                                <Input
                                                    type="number"
                                                    min={0}
                                                    placeholder="e.g. 60"
                                                    value={form.config?.min_delay_seconds ?? ''}
                                                    onChange={(e) => setForm({ ...form, config: { ...form.config, min_delay_seconds: Math.max(0, parseInt(e.target.value) || 0) } })}
                                                    className="rounded-lg mt-1"
                                                />
                                            </div>
                                            <div>
                                                <Label>Maximum delay (seconds)</Label>
                                                <Input
                                                    type="number"
                                                    min={0}
                                                    placeholder="e.g. 300"
                                                    value={form.config?.max_delay_seconds ?? ''}
                                                    onChange={(e) => setForm({ ...form, config: { ...form.config, max_delay_seconds: Math.max(0, parseInt(e.target.value) || 0) } })}
                                                    className="rounded-lg mt-1"
                                                />
                                            </div>
                                            <p className="text-xs text-slate-500 col-span-2">Enter min and max seconds between each email. A random delay in this range is used.</p>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                        {wizardStep === 2 && (
                            <>
                                <div>
                                    <Label className="flex items-center gap-2"><Users className="h-4 w-4" /> Audience (Step 2)</Label>
                                    <p className="text-sm text-slate-500 mt-1 mb-3">Add recipients from one or more sources. Remove any before proceeding.</p>
                                    <div className="space-y-4">
                                        <div className="flex flex-wrap gap-2 items-end">
                                            <Button variant="outline" size="sm" onClick={addMyLeads} disabled={loading} className="rounded-lg">
                                                <UserPlus className="h-4 w-4 mr-2" />
                                                Add My Leads
                                            </Button>
                                            <div className="flex gap-2 items-center flex-1 min-w-[200px]">
                                                <TagSelect value={form.filter_tags || []} onChange={(tags) => setForm({ ...form, filter_tags: tags })} placeholder="Select tags..." />
                                                <Button variant="outline" size="sm" onClick={addByTags} disabled={loading || !form.filter_tags?.length} className="rounded-lg shrink-0">
                                                    Add by Tags
                                                </Button>
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <Textarea
                                                value={form.paste_emails}
                                                onChange={(e) => setForm({ ...form, paste_emails: e.target.value })}
                                                placeholder="Paste emails (one per line or comma-separated)"
                                                className="rounded-lg min-h-[60px] flex-1"
                                            />
                                            <Button variant="outline" size="sm" onClick={addFromPaste} disabled={loading || !form.paste_emails?.trim()} className="rounded-lg shrink-0 self-end">
                                                Add to List
                                            </Button>
                                        </div>
                                        <p className="text-xs text-slate-500">New emails create leads with placeholder data (editable in Lead Details)</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 mb-2">
                                    <Checkbox
                                        id="ignore-status-filters"
                                        checked={form.ignore_status_filters ?? false}
                                        onCheckedChange={(checked) => setForm({ ...form, ignore_status_filters: !!checked })}
                                    />
                                    <Label htmlFor="ignore-status-filters" className="font-normal text-sm">
                                        Include leads in Won / Lost / Do Not Contact (normally skipped)
                                    </Label>
                                </div>
                                <div>
                                    <Label>Final List ({form.audienceList?.length || 0} recipients)</Label>
                                    <ScrollArea className="h-[200px] rounded-lg border mt-1 p-2">
                                        {(!form.audienceList || form.audienceList.length === 0) ? (
                                            <p className="text-sm text-slate-500 py-4 text-center">Add recipients using the options above</p>
                                        ) : (
                                            <div className="space-y-1">
                                                {form.audienceList.map((item) => {
                                                    const { skip_reason, status_label, has_contacted } = getLeadEnrichment(item);
                                                    return (
                                                        <div key={item.leadId} className="flex items-center justify-between py-2 px-2 rounded-md hover:bg-slate-50 group">
                                                            <div className="min-w-0 flex-1">
                                                                <span className="font-medium text-slate-900 truncate block">{item.name}</span>
                                                                <span className="text-xs text-slate-500 truncate block">{item.email}</span>
                                                                <div className="flex flex-wrap gap-1 mt-1">
                                                                    {skip_reason && (
                                                                        <Badge variant="outline" className="text-xs bg-amber-50 text-amber-800 border-amber-200">
                                                                            {skip_reason}
                                                                        </Badge>
                                                                    )}
                                                                    {has_contacted && (
                                                                        <Link
                                                                            to={`/leads/${item.leadId}`}
                                                                            className="inline-flex items-center rounded-md px-1.5 py-0.5 text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200"
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        >
                                                                            Previously contacted →
                                                                        </Link>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <Button variant="ghost" size="sm" onClick={() => removeFromAudience(item.leadId)} className="text-red-600 hover:text-red-700 hover:bg-red-50 shrink-0" title="Remove from list">
                                                                <X className="h-4 w-4" />
                                                            </Button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </ScrollArea>
                                </div>
                            </>
                        )}
                        {wizardStep === 3 && (
                            <>
                                <div>
                                    <Label className="flex items-center gap-2"><FileText className="h-4 w-4" /> Content (Step 3)</Label>
                                    <div className="mt-2 space-y-2">
                                        <div>
                                            <Label>Subject</Label>
                                            <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Hi {{first_name}}..." className="rounded-lg mt-1" />
                                        </div>
                                        <div>
                                            <Label>Body</Label>
                                            <div className="min-h-[200px] rounded-lg mt-1 border p-2">
                                                <RichTextEditor content={form.body_html} onChange={(html) => setForm({ ...form, body_html: html })} placeholder="Write your email..." showVariablePicker={true} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <Button variant="outline" onClick={checkDeliverability} disabled={loading}>
                                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                                        Check Deliverability
                                    </Button>
                                    {deliverabilityResult && (
                                        <div className="flex-1 p-3 rounded-lg bg-slate-50 text-sm">
                                            Score: {deliverabilityResult.score}/10 | Risky: {deliverabilityResult.risky_words?.join(', ') || 'None'} | {deliverabilityResult.suggestions}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <Label>Preview for Lead</Label>
                                    <Select value={previewLead ? String(previewLead.leadId) : ''} onValueChange={(id) => setPreviewLead((form.audienceList || []).find(l => String(l.leadId) === id) ?? null)}>
                                        <SelectTrigger className="rounded-lg mt-1"><SelectValue placeholder="Select lead to preview" /></SelectTrigger>
                                        <SelectContent>
                                            {(form.audienceList || []).slice(0, 20).map((l) => (
                                                <SelectItem key={l.leadId} value={String(l.leadId)}>{l.name} ({l.email})</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {previewLead && (form.subject || form.body_html) && (
                                        <ScrollArea className="mt-2 h-[240px] rounded-lg border bg-slate-50 p-3">
                                            <div className="space-y-3 text-sm pr-4">
                                                {form.subject && (
                                                    <div>
                                                        <strong className="text-slate-700">Subject:</strong>
                                                        <p className="mt-1 text-slate-900">{processVariablesForPreview(form.subject, previewLead)}</p>
                                                    </div>
                                                )}
                                                {form.body_html && (
                                                    <div>
                                                        <strong className="text-slate-700">Body:</strong>
                                                        <div
                                                            className="mt-1 text-slate-900 text-sm [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1 [&_a]:text-blue-600 [&_a]:underline"
                                                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(processVariablesForPreview(form.body_html, previewLead)) }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </ScrollArea>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                    <DialogFooter>
                        {wizardStep > 1 && (
                            <Button variant="outline" onClick={() => setWizardStep(wizardStep - 1)}>
                                <ChevronLeft className="h-4 w-4 mr-1" /> Back
                            </Button>
                        )}
                        <Button onClick={handleCreateCampaign} disabled={loading}>
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : wizardStep < 3 ? <>Next <ChevronRight className="h-4 w-4 ml-1" /></> : 'Create Campaign'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CampaignsPage;
